/**
 * <wellf-buy-card> - one-time vs subscribe toggle + Ajax add-to-cart.
 * Progressive enhancement: the inner <form> posts to /cart/add natively if JS fails.
 * Subscribe uses the product's Appstle selling plan (passed as data-plan).
 */
class WellfBuyCard extends HTMLElement {
  connectedCallback() {
    this.data = this.dataset;
    this.modeBtns = this.querySelectorAll('[data-buy-mode]');
    this.amountEl = this.querySelector('[data-buy-amount]');
    this.perEl = this.querySelector('[data-buy-per]');
    this.wasEl = this.querySelector('[data-buy-was]');
    this.planInput = this.querySelector('[data-buy-plan-input]');
    this.subOnly = this.querySelectorAll('[data-buy-subonly]');
    this.oneOnly = this.querySelectorAll('[data-buy-onetimeonly]');
    this.addBtn = this.querySelector('[data-buy-add]');
    this.form = this.querySelector('form');
    this.addLabelDefault = this.addBtn ? this.addBtn.textContent.trim() : '';

    this.setupDial();

    this.modeBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.setMode(btn.dataset.buyMode));
    });

    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    this.setMode(this.data.default === 'onetime' ? 'onetime' : 'sub', true);
  }

  setMode(mode, silent) {
    const sub = mode === 'sub' && !!this.data.moneySub;
    this.mode = sub ? 'sub' : 'onetime';

    this.modeBtns.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.buyMode === this.mode ? 'true' : 'false');
    });

    if (this.amountEl) this.amountEl.textContent = sub ? this.data.moneySub : this.data.moneyOnetime;
    if (this.perEl) this.perEl.hidden = !sub;
    if (this.wasEl) this.wasEl.hidden = !sub;
    // Club-only extras: savings pill, delivery/gifts perks, free-gift ladder.
    this.subOnly.forEach((el) => { el.hidden = !sub; });
    // One-time-only copy (e.g. the delivery note that Club replaces).
    this.oneOnly.forEach((el) => { el.hidden = sub; });
    if (this.planInput) this.planInput.value = sub ? (this.data.plan || '') : '';
    if (this.addBtn && !this.addBtn.disabled) {
      this.addBtn.textContent = sub ? this.data.labelSub : this.data.labelOnetime;
      this.addLabelDefault = this.addBtn.textContent;
    }
    this.renderDial();
  }

  /* ── Market price comparison: horizontal ruler (v20). Price per gram of creatine
     (delivery folded in) plotted against the market range. Config comes from the
     [data-ppg] element; the ruler + needle + readout (re)plot on every plan change. ── */
  setupDial() {
    this.ppgEl = this.querySelector('[data-ppg]');
    this.ppgDial = this.querySelector('[data-ppg-dial]');
    this.ppgValEl = this.querySelector('[data-ppg-val]');
    this.ppgMarkEl = this.querySelector('[data-ppg-mark]');
    if (!this.ppgEl || !this.ppgDial || !this.ppgValEl) return;
    const d = this.ppgEl.dataset;
    this.ppgCfg = {
      grams: parseFloat(d.ppgGrams) || 0,
      min: parseFloat(d.ppgMarketMin),
      max: parseFloat(d.ppgMarketMax),
      onetime: parseFloat(d.ppgOnetime),
      onetimeDelivery: parseFloat(d.ppgOnetimeDelivery) || 0,
      sub: d.ppgSub != null ? parseFloat(d.ppgSub) : null,
      subDelivery: parseFloat(d.ppgSubDelivery) || 0,
    };
    this.ppgSlits = 41;
  }

  renderDial() {
    const c = this.ppgCfg;
    if (!c || !this.ppgDial || !this.ppgValEl) return;
    const sub = this.mode === 'sub' && c.sub != null;
    const item = sub ? c.sub : c.onetime;
    const delivery = sub ? c.subDelivery : c.onetimeDelivery;
    // Bad/missing config: hide the gauge rather than render nonsense.
    if (!(item > 0) || !(c.grams > 0) || isNaN(c.min) || isNaN(c.max) || !(c.max > c.min)) {
      if (this.ppgEl) this.ppgEl.hidden = true;
      return;
    }
    const perGram = (item + delivery) / c.grams;
    const pct = Math.max(3, Math.min(97, ((perGram - c.min) / (c.max - c.min)) * 100));

    // Build the ruler once (slits grey→dark, every 4th a major tick) + the needle.
    if (!this.ppgNeedle) {
      const N = this.ppgSlits;
      const lerp = (a, b, t) => Math.round(a + (b - a) * t);
      const frag = document.createDocumentFragment();
      for (let i = 0; i < N; i += 1) {
        const t = i / (N - 1);
        const s = document.createElement('span');
        s.className = 'pv2-gauge__slit' + (i % 4 === 0 ? ' pv2-gauge__slit--maj' : '');
        s.style.background = 'rgb(' + lerp(0xCC, 0x38, t) + ',' + lerp(0xCC, 0x38, t) + ',' + lerp(0xCA, 0x38, t) + ')';
        frag.appendChild(s);
      }
      const needle = document.createElement('span');
      needle.className = 'pv2-gauge__needle';
      frag.appendChild(needle);
      this.ppgDial.appendChild(frag);
      this.ppgNeedle = needle;
    }

    this.ppgNeedle.style.left = pct + '%';
    if (this.ppgMarkEl) this.ppgMarkEl.style.left = Math.max(16, Math.min(84, pct)) + '%';
    const money = (n) => '£' + n.toFixed(2);
    this.ppgValEl.innerHTML = money(perGram) + '<em>/g</em>';
    this.ppgDial.setAttribute('aria-label', money(perGram) + ' per gram of creatine, against a market range of ' + money(c.min) + ' to ' + money(c.max));
  }

  async onSubmit(e) {
    if (!window.fetch || !this.form) return; // native submit
    e.preventDefault();
    if (this.addBtn.disabled) return;

    const body = new FormData(this.form);
    body.append('sections_url', window.location.pathname);
    this.setBusy(true);

    try {
      const res = await fetch(`${this.root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      if (!res.ok) throw new Error('add failed');
      await res.json();
      // Single product / single flavour: go straight to the cart (Club upsell lives there).
      window.location.href = `${this.root()}cart`;
    } catch (_) {
      this.form.submit(); // fall back to a full navigation to /cart
    }
  }

  setBusy(busy) {
    this.addBtn.disabled = busy;
    if (busy) this.addBtn.textContent = 'Adding…';
  }

  flash(msg) {
    this.addBtn.disabled = false;
    this.addBtn.textContent = msg;
    setTimeout(() => { this.addBtn.textContent = this.addLabelDefault; }, 1800);
  }

  root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
}
customElements.define('wellf-buy-card', WellfBuyCard);

/* ── Homepage sticky FAB: show after the hero scrolls past, hide when the buy card
   is in view; mirrors the buy-card price and submits its form (same as the PDP FAB). ── */
(function homeFab() {
  var fab = document.querySelector('[data-home-fab]');
  var hero = document.getElementById('wellf-hero');
  var addBtn = document.querySelector('#wellf-buy [data-buy-add]');
  if (!fab || !hero || !addBtn) return;

  var form = addBtn.closest('form');
  var fabBtn = fab.querySelector('[data-home-fab-add]');
  var fabPrice = fab.querySelector('[data-home-fab-price]');
  var amountEl = document.querySelector('#wellf-buy [data-buy-amount]');

  if (fabBtn && form) {
    fabBtn.addEventListener('click', function () {
      if (form.requestSubmit) form.requestSubmit(); else form.submit();
    });
  }

  function syncPrice() { if (fabPrice && amountEl) fabPrice.textContent = amountEl.textContent.trim(); }
  document.querySelectorAll('#wellf-buy [data-buy-mode]').forEach(function (b) {
    b.addEventListener('click', function () { requestAnimationFrame(syncPrice); });
  });

  if (!('IntersectionObserver' in window)) return;
  var heroPast = false, buyVisible = false;
  function update() { fab.setAttribute('data-show', String(heroPast && !buyVisible)); }
  new IntersectionObserver(function (es) {
    var e = es[0]; heroPast = !e.isIntersecting && e.boundingClientRect.top < 0; update();
  }, { threshold: 0, rootMargin: '-72px 0px 0px 0px' }).observe(hero);
  new IntersectionObserver(function (es) {
    buyVisible = es[0].isIntersecting; update();
  }, { threshold: 0 }).observe(addBtn);
})();
